<template>
  <nav class="bg-white">
    <!-- {{ breadcrumbStore.breadcrumbItems }} -->
    <Breadcrumb :model="breadcrumbStore.breadcrumbItems" class="px-4 py-2" />
  </nav>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import Breadcrumb from 'primevue/breadcrumb'
import type { MenuItem } from 'primevue/menuitem'

export default defineComponent({
  name: 'MOPBreadcrumb',
  components: {
    Breadcrumb
  }
})
</script>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useBreadcrumbStore } from './breadcrumbStore'

const route = useRoute()
const router = useRouter()
const breadcrumbStore = useBreadcrumbStore()

// const home = computed<MenuItem>(() => ({
//   icon: 'pi pi-home',
//   to: '/'
// }))

const formatPathSegment = (segment: string): string => {
  try {
    // Remove hyphens and convert to spaces
    const withoutHyphens = segment.replace(/-/g, ' ')

    // Split by uppercase letters and join with space
    const words = withoutHyphens.split(/(?=[A-Z])/).join(' ')

    // Capitalize first letter of each word
    return words
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  } catch (error) {
    console.error('Error formatting path segment:', error)
    return segment // Return original segment if formatting fails
  }
}

const generateBreadcrumbItems = () => {
  try {
    if (!route.matched) return []

    // const newItems = route.matched.slice(1).map(record => {
    const newItems = route.matched.map(record => {
      const meta = record.meta as { breadcrumb?: string } || {}
      const label = record.name?.toString().toUpperCase()
      const path = record.path === '/accounts' ? '/accounts/dashboard' : record.path
      const item: MenuItem = {
        label,
        path,
        query: { ...route.query } // Store current query parameters
      }
      return item
    }).filter(item => item.label)

    return newItems
  } catch (error) {
    console.error('Error generating breadcrumb items:', error)
    return [] // Return empty array if generation fails
  }
}

// Update breadcrumb items when route changes
watch(
  [() => route.path, () => route.query],
  () => {
    const currentPath = route.path
    const storedItems = breadcrumbStore.breadcrumbItems

    // Check if we're navigating to a path that's already in our breadcrumb trail
    const existingIndex = storedItems.findIndex(item => item.path === currentPath)

    if (existingIndex !== -1) {
      // If we found the path, update its query and trim the breadcrumb trail
      const updatedItems = storedItems.slice(0, existingIndex + 1).map((item, index) => {
        if (index === existingIndex) {
          return { ...item, query: { ...route.query } }
        }
        return item
      })
      breadcrumbStore.setBreadcrumbItems(updatedItems)
    } else {
      // If it's a new path, generate the new item and append it
      const newItems = generateBreadcrumbItems()
      const lastNewItem = newItems[newItems.length - 1]
      if (lastNewItem) {
        breadcrumbStore.setBreadcrumbItems([...storedItems, lastNewItem])
      }
    }
  },
  { immediate: true }
)
</script>

<style scoped>
:deep(.p-breadcrumb) {
  border: none;
  padding: 0;
  background: transparent;
}

:deep(.p-menuitem-text) {
  color: #6B7280;
}

:deep(.p-menuitem-link:hover .p-menuitem-text) {
  color: #00A76F !important;
}

:deep(.router-link-active .p-menuitem-text) {
  color: #00A76F !important;
}

:deep(.p-breadcrumb-home .p-menuitem-text) {
  color: #6B7280;
}

:deep(.p-breadcrumb-home:hover .p-menuitem-text) {
  color: #00A76F !important;
}

:deep(.p-menuitem-link:focus) {
  box-shadow: none !important;
}

:deep(.p-breadcrumb .p-breadcrumb-list li .p-menuitem-link) {
  border-radius: 0;
}

:deep(.p-breadcrumb .p-breadcrumb-list li:last-child .p-menuitem-text) {
  color: #00A76F;
}

:deep(.p-breadcrumb ul li .p-menuitem-link .p-menuitem-text) {
  color: #6B7280;
}

:deep(.p-breadcrumb ul li.p-breadcrumb-chevron) {
  margin: 0 0.5rem;
  color: #6B7280;
}

:deep(.p-breadcrumb ul li:last-child .p-menuitem-text) {
  color: #00A76F;
}

.text-primary {
  color: #00A76F;
}
</style>
